"use client";

import FeatureTemplate from "@/components/feature-template";
import type {
  DetailedFeatureItem,
  FeatureHeroTab,
  TestimonialItem,
} from "@/components/feature-template/types";
import { FileText, Handshake, MessageSquare } from "lucide-react";
import { faqs } from "./content";

const IMG = "/assets/images/resources/career-guides";
const GUIDES = "/blogs";

const heroFeatures: FeatureHeroTab[] = [
  {
    id: "resume-guides",
    name: "Resume Guides",
    title: "Write a Resume That Gets Past the ATS",
    description:
      "Step-by-step guides on layout, keywords and bullet points, with before-and-after examples for every section of your resume.",
    imageSrcUrl: `${IMG}/hero-resume-guides.png`,
    primaryActionText: "Read the Guides",
    primaryActionLink: GUIDES,
    secondaryActionText: "Build My Resume",
    secondaryActionLink: "/resume/build",
  },
  {
    id: "interview-prep",
    name: "Interview Prep",
    title: "Prepare for Every Interview Round",
    description:
      "Common questions by role, the STAR method explained with sample answers, and what interviewers listen for in each one.",
    imageSrcUrl: `${IMG}/hero-interview-prep.png`,
    primaryActionText: "Read the Guides",
    primaryActionLink: GUIDES,
    secondaryActionText: "Try AI Mock Interview",
    secondaryActionLink: "/features/ai-mock-interview",
  },
  {
    id: "negotiation",
    name: "Negotiation",
    title: "Ask for More, in the Right Words",
    description:
      "Scripts for the expected-salary question, the counteroffer and the follow-up email, and when to use each one.",
    imageSrcUrl: `${IMG}/hero-negotiation.png`,
    primaryActionText: "Read the Guides",
    primaryActionLink: GUIDES,
    secondaryActionText: "Salary Insights",
    secondaryActionLink: "/resources/salary-insights",
  },
];

const featureGroup1: DetailedFeatureItem[] = [
  {
    id: "resume-ats-guides",
    title: "Resume and ATS Guides",
    subtitle: "GET PAST THE FILTER",
    description:
      "Learn how applicant tracking systems read a resume, which layouts break them and where keywords belong. Each guide ends with a checklist you can run on your own resume.",
    benefits: [
      "ATS-safe layout rules",
      "Keyword placement by section",
      "Before-and-after rewrites",
      "A checklist for every guide",
    ],
    primaryCTA: "Read Resume Guides",
    primaryLink: GUIDES,
    secondaryCTA: "Score My Resume",
    secondaryLink: "/features/resume-jobfit-scorer",
    imageSrcUrl: `${IMG}/resume-teardown.png`,
    icon: <FileText className="w-4 h-4" />,
    stats: "Before-and-after examples",
  },
  {
    id: "interview-guides",
    title: "Interview Guides",
    subtitle: "ANSWERS THAT HOLD UP",
    description:
      "Question banks for behavioural, technical and case rounds, the STAR method broken down step by step, and sample answers you can adapt to your own experience.",
    benefits: [
      "Questions by role and round",
      "STAR answers, step by step",
      "What interviewers listen for",
      "Practise out loud with AI",
    ],
    primaryCTA: "Read Interview Guides",
    primaryLink: GUIDES,
    secondaryCTA: "Try AI Mock Interview",
    secondaryLink: "/features/ai-mock-interview",
    imageSrcUrl: `${IMG}/question-bank.png`,
    icon: <MessageSquare className="w-4 h-4" />,
    stats: "Behavioural, technical and case",
    orientation: "reversed",
  },
];

const featureGroup2: DetailedFeatureItem[] = [
  {
    id: "negotiation-guides",
    title: "Negotiation and Offer Guides",
    subtitle: "DON'T LEAVE MONEY BEHIND",
    description:
      "What to say when a recruiter asks for your expected salary, how to counter without risking the offer, and how to weigh base pay, bonus and equity.",
    benefits: [
      "Word-for-word scripts",
      "Counteroffer email templates",
      "Timing and anchoring",
      "Comparing total pay",
    ],
    primaryCTA: "Read Negotiation Guides",
    primaryLink: GUIDES,
    secondaryCTA: "Salary Insights",
    secondaryLink: "/resources/salary-insights",
    imageSrcUrl: `${IMG}/counter-offer.png`,
    icon: <Handshake className="w-4 h-4" />,
    stats: "Scripts for every stage",
  },
];

// Replace with real user quotes before publishing.
const testimonials: TestimonialItem[] = [
  {
    id: 1,
    text: "The ATS guide showed me my two-column resume was being read as one long line. I switched layouts and started getting screening calls within a week.",
    name: "Rahul V.",
    role: "Backend Engineer",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1539571696357-5a69c17a67c6?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 2,
    text: "I used the STAR examples to rebuild my answers, then practised them in AI Mock Interview. I finally stopped rambling.",
    name: "Sneha P.",
    role: "UX Researcher",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=600&h=800&fit=crop&q=80",
  },
  {
    id: 3,
    text: "I copied the counteroffer email almost word for word and got a higher base the next day.",
    name: "Ananya R.",
    role: "Product Manager",
    company: "MindMyJob user",
    image:
      "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=600&h=800&fit=crop&q=80",
  },
];

export default function CareerGuidesPageClient() {
  return (
    <FeatureTemplate
      type="type1"
      sections={[
        {
          type: "hero",
          data: {
            title: "Career Advice That",
            titleHighlight: "Gets You Hired.",
            subtitle:
              "Practical guides on resumes, interviews and salary negotiation, with real examples, checklists and scripts you can use today.",
            ctaText: "Read the Guides",
            ctaLink: GUIDES,
            features: heroFeatures,
          },
        },
        {
          type: "features",
          data: {
            sectionTitle: "Learn It. Then Use It.",
            sectionSubtitle:
              "Every guide links to the MindMyJob tool that puts the advice into practice.",
            features: featureGroup1,
          },
        },
        { type: "features", data: { features: featureGroup2 } },
        {
          type: "testimonials",
          data: {
            title: "Advice That Worked",
            subtitle: "From job seekers who put the guides to use",
            testimonials,
          },
        },
        {
          type: "faq",
          data: {
            title: "Questions About Career Guides",
            subtitle: "What the guides cover and how to use them.",
            faqs,
            ctaTitle: "Start with the guide you need most.",
            ctaText: "Read the Guides",
            ctaLink: GUIDES,
          },
        },
      ]}
    />
  );
}
